import { environment } from '@environments/environment';
import { Injectable } from '@angular/core';
import type { ApplicationInsights, IConfig, IConfiguration } from '@microsoft/applicationinsights-web';

@Injectable({
  providedIn: 'root',
})
export class LogService {
  // Application Insights is ~190 kB, so it's loaded in a separate chunk after start instead of in the initial bundle.
  // Calls made before it has loaded wait for it; if it fails to load, logging is silently skipped.
  private ready: Promise<ApplicationInsights | undefined>;

  constructor() {
    this.ready = import('@microsoft/applicationinsights-web')
      .then(({ ApplicationInsights }) => {
        const appInsights = new ApplicationInsights({
          config: {
            connectionString: environment.appInsights.connectionString,
            enableAutoRouteTracking: false,
            // Chrome is phasing out the 'unload' event; the SDK still flushes on 'beforeunload' and 'pagehide'
            disablePageUnloadEvents: ['unload'],
            cookieCfg: {
              enabled: false,
            },
          } as IConfiguration & IConfig,
        });
        appInsights.addTelemetryInitializer(item => {
          item.tags!['ai.cloud.roleInstance'] = environment.appInsights.cloudRoleInstance;
        });
        appInsights.loadAppInsights();
        return appInsights;
      })
      .catch(() => undefined);
  }

  logPageView(name?: string, url?: string) {
    this.ready.then(appInsights =>
      appInsights?.trackPageView({
        name: name,
        uri: url,
      })
    );
  }

  logEvent(name: string, properties?: Record<string, never>) {
    this.ready.then(appInsights => appInsights?.trackEvent({ name: name }, properties));
  }

  logMetric(name: string, average: number, properties?: Record<string, never>) {
    this.ready.then(appInsights => appInsights?.trackMetric({ name: name, average: average }, properties));
  }

  logException(exception: Error, severityLevel?: number) {
    this.ready.then(appInsights =>
      appInsights?.trackException({
        exception: exception,
        severityLevel: severityLevel,
      })
    );
  }

  logTrace(message: string, properties?: Record<string, never>) {
    this.ready.then(appInsights => appInsights?.trackTrace({ message: message }, properties));
  }
}
